import React, { useCallback, useEffect, useState } from 'react';
import { Form, Row, Col, Button, Table, Modal, Spinner } from 'react-bootstrap';
import { IClausulas } from '../index';
import api from '../../../services/api';
import { notify } from '../../../stores/useNotificationStore';
import SearchSelectField, { SearchOption } from '../../../components/SearchSelectField';
import { FiDownload, FiUserPlus, FiEdit2, FiX, FiSend, FiSlash, FiClock } from '../../../utils/icons';

interface ClausulasTabProps {
  clausulas: IClausulas;
  setClausulas: React.Dispatch<React.SetStateAction<IClausulas>>;
  onNdaFileChange: (file: File | null) => void;
  ndaFileName: string;
  contratoId?: number;
  showNdaDownload?: boolean;
  empresaId?: number | null;
}

interface SimNaoProps {
  label: string;
  value: boolean;
  onChange: (val: boolean) => void;
  name: string;
}

interface IEscopoRow {
  id: number;
  contrato_id: number;
  escopo: string;
  representante_nome: string;
  representante_email: string;
  acesso_id: number | null;
  status: string | null;
  questionario_id: number | null;
  questionario_nome: string | null;
  data_alvo_resposta: string | null;
  data_ultima_resposta: string | null;
  enviado_em: string | null;
}

interface IQuestionarioOpt {
  id: number;
  nome: string;
  escopo: string | null;
  is_modelo: boolean;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const STATUS_LABEL: Record<string, string> = {
  ENVIADO: 'Enviado',
  ACESSADO: 'Acessado',
  RESPONDIDO: '',
  FECHADO: 'Fechado',
  REVOGADO: 'Revogado (link inválido)',
};

// Coluna "Data última resposta": mostra a data quando já respondido; caso contrário,
// um lembrete curto do estado do envio (ou — quando nunca foi enviado).
const celulaUltimaResposta = (row: IEscopoRow): React.ReactNode => {
  if (row.status === 'RESPONDIDO') return formatarData(row.data_ultima_resposta);
  if (row.status && STATUS_LABEL[row.status]) return <span className="text-muted small">{STATUS_LABEL[row.status]}</span>;
  return '—';
};

const ATIVOS = ['ENVIADO', 'ACESSADO', 'RESPONDIDO'];

interface ILogEntry {
  id: number;
  evento: string;
  detalhe: string | null;
  ip: string | null;
  criado_em: string;
}

const EVENTO_LABEL: Record<string, string> = {
  ACESSO_LINK: 'Acessou o link',
  CODIGO_SOLICITADO: 'Solicitou código de acesso',
  CODIGO_VALIDADO: 'Validou o código (entrou no questionário)',
  TENTATIVA_ERRADA: 'Digitou um código errado',
  BLOQUEADO: 'Bloqueado por tentativas erradas',
  RESPOSTAS_ENVIADAS: 'Enviou as respostas',
};

const formatarDataHora = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR');
};

const SimNao: React.FC<SimNaoProps> = ({ label, value, onChange, name }) => (
  <Form.Group>
    <Form.Label>{label}</Form.Label>
    <div>
      <Form.Check
        inline
        type="radio"
        label="Sim"
        name={name}
        id={`${name}-sim`}
        checked={value === true}
        onChange={() => onChange(true)}
      />
      <Form.Check
        inline
        type="radio"
        label="Não"
        name={name}
        id={`${name}-nao`}
        checked={value === false}
        onChange={() => onChange(false)}
      />
    </div>
  </Form.Group>
);

const ClausulasTab: React.FC<ClausulasTabProps> = ({
  clausulas, setClausulas, onNdaFileChange, ndaFileName,
  contratoId, showNdaDownload, empresaId,
}) => {
  const set = (field: keyof IClausulas) => (val: boolean) =>
    setClausulas(prev => ({ ...prev, [field]: val }));

  const handleNdaFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    onNdaFileChange(e.target.files?.[0] || null);
  };

  const handleNdaDownload = async () => {
    if (!contratoId) return;
    const res = await api.getContratoDownloadUrl(contratoId, 'nda_arquivo');
    if (res.success && res.data?.url) window.open(res.data.url, '_blank');
  };

  // ── Escopos de Questionário + Representantes do Fornecedor ──
  const [rows, setRows] = useState<IEscopoRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [escopoOptions, setEscopoOptions] = useState<SearchOption[]>([]);
  const [questionarios, setQuestionarios] = useState<IQuestionarioOpt[]>([]);

  // Modal: Adicionar/Editar representante (editandoId != null => edição)
  const [showAddModal, setShowAddModal] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [novoEscopo, setNovoEscopo] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [novoEmail, setNovoEmail] = useState('');
  const [adicionando, setAdicionando] = useState(false);

  // Modal: Enviar Questionário (em lote)
  const [showEnviarModal, setShowEnviarModal] = useState(false);
  const [selecaoEnvio, setSelecaoEnvio] = useState<Record<number, { marcado: boolean; questionarioId: number | '' }>>({});
  const [prazoAlvo, setPrazoAlvo] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Modal: Revogar link (em lote)
  const [showRevogarModal, setShowRevogarModal] = useState(false);
  const [selecaoRevogar, setSelecaoRevogar] = useState<Record<number, boolean>>({});
  const [revogando, setRevogando] = useState(false);

  const [excluindoId, setExcluindoId] = useState<number | null>(null);

  // Modal: Log de auditoria de acessos (Fase 4 — quem acessou esse link e quando)
  const [logModal, setLogModal] = useState<{ acessoId: number; representante: string } | null>(null);
  const [logEntries, setLogEntries] = useState<ILogEntry[]>([]);
  const [carregandoLog, setCarregandoLog] = useState(false);

  const abrirLogModal = async (row: IEscopoRow) => {
    if (!row.acesso_id) return;
    setLogModal({ acessoId: row.acesso_id, representante: row.representante_nome });
    setCarregandoLog(true);
    try {
      const res = await api.getAcessoExternoLog(row.acesso_id);
      setLogEntries(res.success ? (res.data as ILogEntry[]) || [] : []);
    } finally {
      setCarregandoLog(false);
    }
  };

  const carregarRows = useCallback(async () => {
    if (!contratoId) return;
    setLoadingRows(true);
    try {
      const res = await api.getContratoEscopos(contratoId);
      if (res.success) setRows((res.data as IEscopoRow[]) || []);
    } finally {
      setLoadingRows(false);
    }
  }, [contratoId]);

  useEffect(() => {
    carregarRows();
  }, [carregarRows]);

  useEffect(() => {
    api.getCadastrosAuxiliares('escopo_questionario', empresaId ?? undefined).then(res => {
      if (res.success) {
        setEscopoOptions(((res.data as any[]) || [])
          .filter(r => r.ativo !== false)
          .map(r => ({ key: r.nome, label: r.nome })));
      }
    });
    api.getQuestionnaires().then(res => {
      if (res.success) setQuestionarios((res.data as IQuestionarioOpt[]) || []);
    });
  }, [empresaId]);

  const criarEscopoAuxiliar = useCallback(async (nome: string): Promise<SearchOption | null> => {
    if (!empresaId) { notify.warning('Selecione uma empresa antes de cadastrar.'); return null; }
    try {
      const res = await api.createCadastroAuxiliar({ tipo: 'escopo_questionario', nome, empresa_id: empresaId });
      if (res.success) {
        const opt: SearchOption = { key: nome, label: nome };
        setEscopoOptions(prev => [...prev, opt]);
        return opt;
      }
      notify.error((res.error as string) || 'Erro ao cadastrar escopo.');
      return null;
    } catch {
      notify.error('Erro de conexão ao cadastrar escopo.');
      return null;
    }
  }, [empresaId]);

  const emailInvalido = !!novoEmail && !isValidEmail(novoEmail);

  const abrirAddModal = () => {
    setEditandoId(null);
    setNovoEscopo(''); setNovoNome(''); setNovoEmail('');
    setShowAddModal(true);
  };

  const abrirEditarModal = (row: IEscopoRow) => {
    setEditandoId(row.id);
    setNovoEscopo(row.escopo); setNovoNome(row.representante_nome); setNovoEmail(row.representante_email);
    setShowAddModal(true);
  };

  const handleAdicionar = async () => {
    if (!contratoId) return;
    if (!novoEscopo.trim() || !novoNome.trim() || !novoEmail.trim() || emailInvalido) {
      notify.warning('Preencha escopo, nome e um e-mail válido do representante.');
      return;
    }
    setAdicionando(true);
    try {
      const dados = { escopo: novoEscopo.trim(), representante_nome: novoNome.trim(), representante_email: novoEmail.trim() };
      const res = editandoId
        ? await api.updateContratoEscopo(editandoId, dados)
        : await api.createContratoEscopo(contratoId, dados);
      if (res.success) {
        notify.success(editandoId ? 'Representante atualizado.' : 'Representante cadastrado.');
        setShowAddModal(false);
        carregarRows();
      } else {
        notify.error((res.error as string) || 'Erro ao salvar.');
      }
    } catch {
      notify.error('Erro de conexão ao salvar.');
    } finally {
      setAdicionando(false);
    }
  };

  const handleExcluir = async (row: IEscopoRow) => {
    if (!window.confirm(`Remover "${row.representante_nome}" (${row.escopo})? O link enviado, se houver, deixa de funcionar.`)) return;
    setExcluindoId(row.id);
    try {
      const res = await api.deleteContratoEscopo(row.id);
      if (res.success) {
        setRows(prev => prev.filter(r => r.id !== row.id));
      } else {
        notify.error((res.error as string) || 'Erro ao remover.');
      }
    } catch {
      notify.error('Erro de conexão ao remover.');
    } finally {
      setExcluindoId(null);
    }
  };

  // Modelos (is_modelo) são copiados antes de usar em uma empresa, então não fazem
  // sentido como destino de envio direto; qualquer outro questionário com o mesmo
  // escopo já pode ser enviado (o app não tem um fluxo de "publicar" separado).
  const questionariosDoEscopo = (escopo: string) =>
    questionarios.filter(q => q.escopo === escopo && !q.is_modelo);

  // ── Enviar Questionário (em lote) ──
  const abrirEnviarModal = () => {
    const inicial: Record<number, { marcado: boolean; questionarioId: number | '' }> = {};
    rows.forEach(row => {
      const matches = questionariosDoEscopo(row.escopo);
      const escolhido = row.questionario_id && matches.some(m => m.id === row.questionario_id)
        ? row.questionario_id
        : (matches[0]?.id ?? '');
      inicial[row.id] = { marcado: matches.length > 0, questionarioId: escolhido };
    });
    setSelecaoEnvio(inicial);
    setPrazoAlvo('');
    setShowEnviarModal(true);
  };

  const confirmarEnvioLote = async () => {
    const selecionados = rows.filter(r => selecaoEnvio[r.id]?.marcado && selecaoEnvio[r.id]?.questionarioId);
    if (selecionados.length === 0) {
      notify.warning('Selecione ao menos um representante com questionário definido.');
      return;
    }
    setEnviando(true);
    try {
      let ok = 0;
      for (const row of selecionados) {
        const res = await api.enviarQuestionarioEscopo(row.id, {
          questionario_id: Number(selecaoEnvio[row.id].questionarioId),
          data_alvo_resposta: prazoAlvo || undefined,
        });
        if (res.success) ok++;
      }
      notify.success(`Link enviado para ${ok} de ${selecionados.length} representante(s).`);
      setShowEnviarModal(false);
      carregarRows();
    } catch {
      notify.error('Erro de conexão ao enviar.');
    } finally {
      setEnviando(false);
    }
  };

  // ── Revogar link (em lote) ──
  const linhasComLinkAtivo = rows.filter(r => r.status && ATIVOS.includes(r.status));

  const abrirRevogarModal = () => {
    setSelecaoRevogar({});
    setShowRevogarModal(true);
  };

  const confirmarRevogacaoLote = async () => {
    const selecionados = linhasComLinkAtivo.filter(r => selecaoRevogar[r.id]);
    if (selecionados.length === 0) {
      notify.warning('Selecione ao menos um representante para revogar.');
      return;
    }
    setRevogando(true);
    try {
      let ok = 0;
      for (const row of selecionados) {
        const res = await api.revogarQuestionarioEscopo(row.id);
        if (res.success) ok++;
      }
      notify.success(`${ok} link(s) revogado(s).`);
      setShowRevogarModal(false);
      carregarRows();
    } catch {
      notify.error('Erro de conexão ao revogar.');
    } finally {
      setRevogando(false);
    }
  };

  return (
    <Form>
      <Row className="g-4">
        <Col md={6}>
          <SimNao
            label="Cláusulas de Privacidade / LGPD"
            name="clausulas_privacidade"
            value={clausulas.clausulas_privacidade}
            onChange={set('clausulas_privacidade')}
          />
        </Col>
        <Col md={6}>
          <SimNao
            label="Cláusulas de Segurança da Informação"
            name="clausulas_seguranca"
            value={clausulas.clausulas_seguranca}
            onChange={set('clausulas_seguranca')}
          />
        </Col>
        <Col md={6}>
          <SimNao
            label="Adequado à LGPD – se Operador"
            name="adequado_lgpd"
            value={clausulas.adequado_lgpd}
            onChange={set('adequado_lgpd')}
          />
        </Col>
        <Col md={6}>
          <SimNao
            label="Termo de Confidencialidade – NDA"
            name="nda"
            value={clausulas.nda}
            onChange={set('nda')}
          />
        </Col>
        {clausulas.nda && (
          <Col md={12}>
            <Form.Group controlId="ndaUpload">
              <Form.Label>Upload do Termo de Confidencialidade (NDA)</Form.Label>
              <Form.Control type="file" accept=".pdf,.doc,.docx" onChange={handleNdaFile} />
              {ndaFileName && (
                <div className="d-flex align-items-center gap-2 mt-1">
                  <Form.Text className="text-muted mb-0">Arquivo atual: {ndaFileName}</Form.Text>
                  {showNdaDownload && (
                    <Button variant="outline-secondary" size="sm" onClick={handleNdaDownload}><FiDownload /> Baixar</Button>
                  )}
                </div>
              )}
            </Form.Group>
          </Col>
        )}

        <Col md={12}>
          <div className="border rounded p-3 bg-light">
            <div className="d-flex justify-content-between align-items-start mb-1">
              <div>
                <h6 className="mb-1">Representantes</h6>
                <p className="text-muted small mb-0">
                  Aqui lista todos os representantes do Fornecedor e seus respectivos Questionários.
                </p>
              </div>
              {contratoId && (
                <Button variant="outline-primary" size="sm" onClick={abrirAddModal} style={{ whiteSpace: 'nowrap' }}>
                  <FiUserPlus /> Representante
                </Button>
              )}
            </div>

            {!contratoId ? (
              <div className="text-muted small mt-2">Salve o cadastro do fornecedor antes de definir escopos e representantes.</div>
            ) : loadingRows ? (
              <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : rows.length === 0 ? (
              <div className="text-muted small mt-2">Nenhum representante cadastrado ainda.</div>
            ) : (
              <div style={{ overflowX: 'auto' }} className="mt-3">
                <Table size="sm" bordered className="mb-0 bg-white">
                  <thead>
                    <tr>
                      <th>Escopo do Questionário</th>
                      <th>Nome do representante da Contratada</th>
                      <th>E-mail do representante</th>
                      <th>Data última resposta</th>
                      <th style={{ width: 70 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={row.id}>
                        <td>{row.escopo}</td>
                        <td>{row.representante_nome}</td>
                        <td>{row.representante_email}</td>
                        <td>{celulaUltimaResposta(row)}</td>
                        <td className="text-center text-nowrap">
                          {row.acesso_id && (
                            <Button
                              variant="link" size="sm" className="p-0 me-2" title="Log de acessos"
                              onClick={() => abrirLogModal(row)}
                            >
                              <FiClock />
                            </Button>
                          )}
                          <Button
                            variant="link" size="sm" className="p-0 me-2" title="Editar representante"
                            onClick={() => abrirEditarModal(row)}
                          >
                            <FiEdit2 />
                          </Button>
                          <Button
                            variant="link" size="sm" className="text-danger p-0" title="Remover representante"
                            disabled={excluindoId === row.id}
                            onClick={() => handleExcluir(row)}
                          >
                            {excluindoId === row.id ? <Spinner as="span" size="sm" animation="border" /> : <FiX />}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {contratoId && rows.length > 0 && (
              <div className="mt-3 pt-3 border-top">
                <h6 className="mb-1">Questionários de conformidade</h6>
                <p className="text-muted small mb-2">
                  Envie um questionário de avaliação de conformidade para o representante da contratada.
                </p>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" onClick={abrirEnviarModal}><FiSend /> Enviar Questionário</Button>
                  <Button variant="outline-danger" onClick={abrirRevogarModal} disabled={linhasComLinkAtivo.length === 0}>
                    <FiSlash /> Revogar link do Questionário
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* ── Modal: Adicionar/Editar Representante ── */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editandoId ? 'Editar Representante' : 'Adicionar Representante'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Escopo do Questionário</Form.Label>
            <SearchSelectField
              placeholder="Pesquisar ou cadastrar escopo..."
              options={escopoOptions}
              value={novoEscopo}
              onSelect={opt => setNovoEscopo(opt ? opt.key : '')}
              onAdd={criarEscopoAuxiliar}
              allowFreeText
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Nome do representante</Form.Label>
            <Form.Control value={novoNome} onChange={e => setNovoNome(e.target.value)} placeholder="Nome completo" />
          </Form.Group>
          <Form.Group>
            <Form.Label>E-mail do representante</Form.Label>
            <Form.Control
              type="email" value={novoEmail} onChange={e => setNovoEmail(e.target.value)}
              placeholder="email@contratada.com.br" isInvalid={emailInvalido}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleAdicionar} disabled={adicionando}>
            {adicionando ? <Spinner as="span" size="sm" animation="border" /> : (editandoId ? 'Salvar' : 'Adicionar')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal: Enviar Questionário (em lote) ── */}
      <Modal show={showEnviarModal} onHide={() => setShowEnviarModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Enviar Questionário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Selecione os representantes que devem receber o link e o questionário correspondente ao escopo de cada um.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <Table size="sm" bordered className="mb-3">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Escopo</th>
                  <th>Representante</th>
                  <th>Questionário</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const matches = questionariosDoEscopo(row.escopo);
                  const sel = selecaoEnvio[row.id] || { marcado: false, questionarioId: '' };
                  return (
                    <tr key={row.id}>
                      <td>
                        <Form.Check
                          checked={sel.marcado}
                          disabled={matches.length === 0}
                          onChange={e => setSelecaoEnvio(prev => ({ ...prev, [row.id]: { ...sel, marcado: e.target.checked } }))}
                        />
                      </td>
                      <td>{row.escopo}</td>
                      <td>{row.representante_nome}<div className="text-muted small">{row.representante_email}</div></td>
                      <td>
                        {matches.length === 0 ? (
                          <span className="text-muted small">Nenhum questionário cadastrado com este escopo.</span>
                        ) : (
                          <Form.Select
                            size="sm"
                            value={sel.questionarioId}
                            onChange={e => setSelecaoEnvio(prev => ({ ...prev, [row.id]: { ...sel, questionarioId: Number(e.target.value) } }))}
                          >
                            {matches.map(q => <option key={q.id} value={q.id}>{q.nome}</option>)}
                          </Form.Select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <Form.Group>
            <Form.Label>Prazo alvo para resposta (opcional, aplicado aos selecionados)</Form.Label>
            <Form.Control type="date" value={prazoAlvo} onChange={e => setPrazoAlvo(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEnviarModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={confirmarEnvioLote} disabled={enviando}>
            {enviando ? <Spinner as="span" size="sm" animation="border" /> : 'Confirmar e Enviar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal: Revogar link (em lote) ── */}
      <Modal show={showRevogarModal} onHide={() => setShowRevogarModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Revogar link do Questionário</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            O link selecionado deixa de funcionar imediatamente para o representante.
          </p>
          {linhasComLinkAtivo.length === 0 ? (
            <div className="text-muted small">Nenhum link ativo no momento.</div>
          ) : (
            linhasComLinkAtivo.map(row => (
              <Form.Check
                key={row.id}
                className="mb-2"
                label={`${row.representante_nome} — ${row.escopo} (${row.representante_email})`}
                checked={!!selecaoRevogar[row.id]}
                onChange={e => setSelecaoRevogar(prev => ({ ...prev, [row.id]: e.target.checked }))}
              />
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRevogarModal(false)}>Cancelar</Button>
          <Button variant="danger" onClick={confirmarRevogacaoLote} disabled={revogando || linhasComLinkAtivo.length === 0}>
            {revogando ? <Spinner as="span" size="sm" animation="border" /> : 'Revogar selecionados'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal: Log de auditoria de acessos (quem acessou o link e quando) ── */}
      <Modal show={!!logModal} onHide={() => setLogModal(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Log de acessos — {logModal?.representante}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {carregandoLog ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
          ) : logEntries.length === 0 ? (
            <div className="text-muted small">Nenhum acesso registrado ainda para este link.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table size="sm" bordered className="mb-0">
                <thead>
                  <tr>
                    <th>Evento</th>
                    <th>Data/hora</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logEntries.map(entry => (
                    <tr key={entry.id}>
                      <td>{EVENTO_LABEL[entry.evento] || entry.evento}</td>
                      <td className="text-nowrap">{formatarDataHora(entry.criado_em)}</td>
                      <td className="text-muted small">{entry.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLogModal(null)}>Fechar</Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export default ClausulasTab;
